import {
  CountryFormConfiguration,
  DEFAULT_ACCEPTED_ATTACHMENT_EXTENSIONS,
} from "../constants";
import dayjs from "dayjs";
import { defaultForbiddenWordsValidation } from "./defaults";
import { Range, SanitizeMode } from "../next-gen-config-wip";
import { AttachmentType } from "../../features/personal-info/attachments/attachmentsStore";
import { UPLOAD_FILE_INVALID_EXTENSION } from "../../features/personal-info/attachments/validations/attachmentValidations";
import { DsaLogo } from "../../components/common/Logo/models/Logo";
import { getAddressData } from "../../api/avs";
import { PersonalInfoValues } from "../../features/personal-info/mutations/model";

const THAI_NAMES_PATTERN = RegExp(/[\u0E00-\u0E7F\p{L}\s\-'ʻ`‘\d]*$/u, "u");
const THAI_ADDRESS_PATTERN = RegExp(
  /[\u0E00-\u0E7F\p{L}\s\-‘.@,/'#()\d]*$/u,
  "u"
);

const getProvinces = async () => {
  const provinces = await getAddressData("th-TH");
  return provinces.values;
};

function validatePostalCode(
  this: { parent: PersonalInfoValues },
  value: string | undefined
) {
  if (!value) {
    return false;
  }

  const { province } = this.parent;

  const bangkokPostalCodes = [
    10600, 10510, 10110, 10230, 10900, 10150, 10210, 10400, 10300, 10170, 10170,
    10140, 10600, 10700, 10600, 10240, 10150, 10120, 10800, 10260, 10150, 10700,
    10500, 10220, 10160, 10240, 10330, 10250, 10100, 10400, 10200, 10260, 10160,
    10510, 10120, 10400, 10140, 10520, 10230, 10310, 10110, 10250, 10240, 10100,
    10120, 10220, 10530, 10160, 10210, 10310,
  ];

  const samutPrakanPostalCodes = [10130, 10270, 10290, 10540, 10540, 10560];

  const pathumThaniPostalCodeRange = { start: 12000, end: 12170 };

  const nonthaburiPostalCodeRange = { start: 11000, end: 11150 };

  const provinceRanges: Range[] = [
    { start: 13000, end: 13270 },
    { start: 14000, end: 14160 },
    { start: 15000, end: 15250 },
    { start: 16000, end: 16150 },
    { start: 17000, end: 17150 },
    { start: 18000, end: 18220 },
    { start: 20000, end: 20270 },
    { start: 21000, end: 21210 },
    { start: 22000, end: 22210 },
    { start: 23000, end: 23140 },
    { start: 24000, end: 24190 },
    { start: 25000, end: 25220 },
    { start: 26000, end: 26130 },
    { start: 27000, end: 27260 },
    { start: 30000, end: 30440 },
    { start: 31000, end: 31260 },
    { start: 32000, end: 32230 },
    { start: 33000, end: 33270 },
    { start: 34000, end: 34360 },
    { start: 35000, end: 35170 },
    { start: 36000, end: 36260 },
    { start: 37000, end: 37290 },
    { start: 38000, end: 38220 },
    { start: 39000, end: 39270 },
    { start: 40000, end: 40350 },
    { start: 41000, end: 41380 },
    { start: 42000, end: 42240 },
    { start: 43000, end: 43220 },
    { start: 44000, end: 44210 },
    { start: 45000, end: 45250 },
    { start: 46000, end: 46240 },
    { start: 47000, end: 47290 },
    { start: 48000, end: 48190 },
    { start: 49000, end: 49160 },
    { start: 50000, end: 50360 },
    { start: 51000, end: 51180 },
    { start: 52000, end: 52240 },
    { start: 53000, end: 53230 },
    { start: 54000, end: 54170 },
    { start: 55000, end: 55220 },
    { start: 56000, end: 56160 },
    { start: 57000, end: 57340 },
    { start: 58000, end: 58150 },
    { start: 60000, end: 60230 },
    { start: 61000, end: 61170 },
    { start: 62000, end: 62210 },
    { start: 63000, end: 63170 },
    { start: 64000, end: 64180 },
    { start: 65000, end: 65190 },
    { start: 66000, end: 66210 },
    { start: 67000, end: 67270 },
    { start: 70000, end: 70180 },
    { start: 71000, end: 71260 },
    { start: 72000, end: 72240 },
    { start: 73000, end: 73170 },
    { start: 74000, end: 74120 },
    { start: 75000, end: 75120 },
    { start: 76000, end: 76170 },
    { start: 77000, end: 77180 },
    { start: 80000, end: 80360 },
    { start: 81000, end: 81160 },
    { start: 82000, end: 82180 },
    { start: 83000, end: 83120 },
    { start: 84000, end: 84350 },
    { start: 85000, end: 85130 },
    { start: 86000, end: 86220 },
    { start: 90000, end: 90310 },
    { start: 91000, end: 91160 },
    { start: 92000, end: 92220 },
    { start: 93000, end: 93190 },
    { start: 94000, end: 94230 },
    { start: 95000, end: 95150 },
    { start: 96000, end: 96220 },
  ];

  switch (province) {
    case "Bangkok - กรุงเทพมหานคร":
      return bangkokPostalCodes.includes(Number(value));
    case "Pathum Thani - ปทุมธานี":
      return (
        pathumThaniPostalCodeRange.start <= Number(value) &&
        pathumThaniPostalCodeRange.end >= Number(value)
      );
    case "Samut Prakan - สมุทรปราการ":
      return samutPrakanPostalCodes.includes(Number(value));
    case "Nonthaburi - นนทบุรี":
      return (
        nonthaburiPostalCodeRange.start <= Number(value) &&
        nonthaburiPostalCodeRange.end >= Number(value)
      );
    default:
      return provinceRanges.some(
        (range) => range.start <= Number(value) && range.end >= Number(value)
      );
  }
}

export const Thailand: CountryFormConfiguration = {
  id: "th-TH",
  forms: {
    personalInfo: {
      beenMemberBefore: {
        name: "beenMemberBefore",
        label: "PREVIOUS_MEMBERSHIP",
        labelExtraClassNames: ["enlargedLabelText"],
        type: "radio",
        options: [
          { value: true, label: "YES" },
          { value: false, label: "NO" },
        ],
        validation: { required: { value: true, errorMessage: "REQUIRED" } },
        classNames: ["centerJustified"],
        validationErrorStyling: ["textCentered"],
      },
      firstNameTransliterated: {
        name: "firstNameTransliterated",
        label: "FIRST_NAME_TRANSLITERATED",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) => value.replace(/[^a-zA-Z\s\-‘ ]/g, ""),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "FIRST_NAME_SIZE",
          },
          maxLength: {
            value: 20,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: /^[a-zA-Z\s\-‘ ]*$/u,
            errorMessage: "FIRST_NAME_SIZE",
          },
          trim: true,
        },
      },
      lastNameTransliterated: {
        name: "lastNameTransliterated",
        label: "LAST_NAME_TRANSLITERATED",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) => value.replace(/[^a-zA-Z\s\-‘ ]/g, ""),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "LAST_NAME_SIZE",
          },
          maxLength: {
            value: 30,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: /^[a-zA-Z\s\-‘ ]*$/u,
            errorMessage: "LAST_NAME_SIZE",
          },
          trim: true,
        },
      },
      firstName: {
        name: "firstName",
        label: "FIRST_NAME",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(RegExp(/[^\u0E00-\u0E7F\s\-‘ ]/, "gu"), ""),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "FIRST_NAME_SIZE",
          },
          maxLength: {
            value: 20,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: THAI_NAMES_PATTERN,
            errorMessage: "FIRST_NAME_SIZE",
          },
          trim: true,
        },
      },
      lastName: {
        name: "lastName",
        label: "LAST_NAME",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(RegExp(/[^\u0E00-\u0E7F\s\-‘ ]/, "gu"), ""),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "LAST_NAME_SIZE",
          },
          maxLength: {
            value: 30,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: THAI_NAMES_PATTERN,
            errorMessage: "LAST_NAME_SIZE",
          },
          trim: true,
        },
      },
      dateOfBirth: {
        name: "dateOfBirth",
        label: "BIRTHDAY",
        type: "date",
        bottomDescription: { text: "BIRTHDAY_DISCLAIMER" },
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxDate: {
            value: dayjs().subtract(20, "year"),
            errorMessage: "TOO_YOUNG",
          },
          minDate: {
            value: dayjs().subtract(120, "year"),
            errorMessage: "TOO_OLD",
          },
        },
      },
      registrationNumber0: {
        name: "registrationNumber0",
        label: "REGISTRATION_NUMBER0",
        type: "text",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 13,
            errorMessage: "REGISTRATION_NUMBER_SIZE",
          },
          minLength: {
            value: 13,
            errorMessage: "REGISTRATION_NUMBER_SIZE",
          },
          pattern: {
            value: /^([0-9]{13})$/u,
            errorMessage: "REGISTRATION_NUMBER_INVALID_PATTERN",
          },
          trim: true,
          mask: "0000000000000",
        },
      },
      registrationNumber0Attachment0: {
        name: "registrationNumber0-attachment0",
        label: "UPLOAD_FILE_INFO_RESIDENT0",
        type: "file",
        index: 0,
        attachmentType: AttachmentType.AUTHORIZATION_DOCUMENT,
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          acceptedExtensions: {
            value: DEFAULT_ACCEPTED_ATTACHMENT_EXTENSIONS,
            errorMessage: UPLOAD_FILE_INVALID_EXTENSION,
          },
        },
      },
      addressLine1Transliterated: {
        name: "addressLine1Transliterated",
        label: "ADDRESS_TRANSLITERATED",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(/[^a-zA-Z\-‘.@,/'#()\d ]/g, ""),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 40,
            errorMessage: "ADDRESS_TRANSLITERATED_SIZE",
          },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "REQUIRED",
          },
          pattern: {
            value: /^[a-zA-Z\s\-‘.@,/'#()\d ]+$/u,
            errorMessage: "ADDRESS_TRANSLITERATED_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      addressLine2Transliterated: {
        name: "addressLine2Transliterated",
        label: "ADDRESS_LINE_2_TRANSLITERATED",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(/[^a-zA-Z\-‘.@,/'#()\d ]/g, ""),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 40,
            errorMessage: "ADDRESS_LINE_2_TRANSLITERATED_SIZE",
          },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "REQUIRED",
          },
          pattern: {
            value: /^[a-zA-Z\s\-‘.@,/'#()\d ]+$/u,
            errorMessage: "ADDRESS_LINE_2_TRANSLITERATED_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      addressLine3Transliterated: {
        name: "addressLine3Transliterated",
        label: "SUBDISTRICT_DISTRICT_TRANSLITERATED",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(/[^a-zA-Z\-‘.@,/'#()\d ]/g, ""),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 30,
            errorMessage: "SUBDISTRICT_DISTRICT_TRANSLITERATED_SIZE",
          },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "REQUIRED",
          },
          pattern: {
            value: /^[a-zA-Z\s\-‘.@,/'#()\d ]+$/u,
            errorMessage: "SUBDISTRICT_DISTRICT_TRANSLITERATED_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      cityTransliterated: {
        name: "cityTransliterated",
        label: "DISTRICT_SUBDISTRICT_TRANSLITERATED",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(/[^a-zA-Z\-‘.@,/'#()\d ]/g, ""),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 30,
            errorMessage: "DISTRICT_SUBDISTRICT_TRANSLITERATED_SIZE",
          },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "REQUIRED",
          },
          pattern: {
            value: /^[a-zA-Z\s\-‘.@,/'#()\d ]+$/u,
            errorMessage: "DISTRICT_SUBDISTRICT_TRANSLITERATED_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      province: {
        name: "province",
        label: "PROVINCE",
        type: "asyncAutocomplete",
        defaultValue: "",
        getOptions: getProvinces,
        sanitize: [
          {
            mode: SanitizeMode.ON_SUBMIT,
            sanitizeFn: (value) => {
              return {
                province: value?.split(" - ")[1],
                provinceTransliterated: value?.split(" - ")[0],
              };
            },
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
      },
      postalCode: {
        name: "postalCode",
        label: "POSTAL_CODE",
        type: "text",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: { value: 5, errorMessage: "POSTAL_CODE_SIZE" },
          minLength: { value: 5, errorMessage: "POSTAL_CODE_SIZE" },
          customValidation: {
            function: validatePostalCode,
            errorMessage: "POSTAL_CODE_SIZE",
          },
        },
      },
      addressLine1: {
        name: "addressLine1",
        label: "ADDRESS",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(
                RegExp(/[^\u0E00-\u0E7F\s\-‘.@,/'#()\d ]/, "gu"),
                ""
              ),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "ADDRESS_SIZE",
          },
          maxLength: {
            value: 40,
            errorMessage: "ADDRESS_SIZE",
          },
          pattern: {
            value: THAI_ADDRESS_PATTERN,
            errorMessage: "ADDRESS_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      addressLine2: {
        name: "addressLine2",
        label: "ADDRESS_LINE_2",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(
                RegExp(/[^\u0E00-\u0E7F\s\-‘.@,/'#()\d ]/, "gu"),
                ""
              ),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "ADDRESS_SIZE",
          },
          maxLength: {
            value: 40,
            errorMessage: "ADDRESS_SIZE",
          },
          pattern: {
            value: THAI_ADDRESS_PATTERN,
            errorMessage: "ADDRESS_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      addressLine3: {
        name: "addressLine3",
        label: "SUBDISTRICT_DISTRICT",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(
                RegExp(/[^\u0E00-\u0E7F\s\-‘.@,/'#()\d ]/, "gu"),
                ""
              ),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 30,
            errorMessage: "ADDRESS_SIZE",
          },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "REQUIRED",
          },
          pattern: {
            value: THAI_ADDRESS_PATTERN,
            errorMessage: "ADDRESS_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      city: {
        name: "city",
        label: "DISTRICT_SUBDISTRICT",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(
                RegExp(/[^\u0E00-\u0E7F\s\-‘.@,/'#()\d ]/, "gu"),
                ""
              ),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 30,
            errorMessage: "ADDRESS_SIZE",
          },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "REQUIRED",
          },
          pattern: {
            value: THAI_ADDRESS_PATTERN,
            errorMessage: "ADDRESS_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      marketingConsentAccepted: {
        name: "marketingConsentAccepted",
        label: "EXTRA_COMMUNICATION_CONSENT",
        type: "checkbox",
        validation: {
          required: { value: false },
        },
      },
    },
    shippingAddress: {
      careOfName: {
        name: "careOfName",
        label: "HMP_CARE_OF_NAME",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(RegExp(/[^a-zA-Z\u0E00-\u0E7F\s\-‘ ]/, "gu"), ""),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 53,
            errorMessage: "HMP_CARE_OF_NAME_SIZE",
          },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "REQUIRED",
          },
          trim: true,
        },
      },
      addressLine1: {
        name: "addressLine1",
        label: "ADDRESS",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(
                RegExp(/[^a-zA-Z\u0E00-\u0E7F\s\-‘.@,/'#()\d ]/, "gu"),
                ""
              ),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "ADDRESS_SIZE",
          },
          maxLength: {
            value: 40,
            errorMessage: "ADDRESS_SIZE",
          },
          pattern: {
            value: /^[a-zA-Z\u0E00-\u0E7F\p{L}\s\-‘.@,/'#()\d ]+$/u,
            errorMessage: "ADDRESS_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      addressLine2: {
        name: "addressLine2",
        label: "ADDRESS_LINE_2",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(
                RegExp(/[^a-zA-Z\u0E00-\u0E7F\s\-‘.@,/'#()\d ]/, "gu"),
                ""
              ),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "ADDRESS_SIZE",
          },
          maxLength: {
            value: 40,
            errorMessage: "ADDRESS_SIZE",
          },
          pattern: {
            value: /^[a-zA-Z\u0E00-\u0E7F\p{L}\s\-‘.@,/'#()\d ]+$/u,
            errorMessage: "ADDRESS_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      addressLine3: {
        name: "addressLine3",
        label: "SUBDISTRICT_DISTRICT",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(
                RegExp(/[^a-zA-Z\u0E00-\u0E7F\s\-‘.@,/'#()\d ]/, "gu"),
                ""
              ),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 30,
            errorMessage: "ADDRESS_SIZE",
          },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "REQUIRED",
          },
          pattern: {
            value: /^[a-zA-Z\u0E00-\u0E7F\p{L}\s\-‘.@,/'#()\d ]+$/u,
            errorMessage: "ADDRESS_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      city: {
        name: "city",
        label: "DISTRICT_SUBDISTRICT",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(
                RegExp(/[^a-zA-Z\u0E00-\u0E7F\s\-‘.@,/'#()\d ]/, "gu"),
                ""
              ),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 30,
            errorMessage: "ADDRESS_SIZE",
          },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "REQUIRED",
          },
          pattern: {
            value: /^[a-zA-Z\u0E00-\u0E7F\p{L}\s\-‘.@,/'#()\d ]+$/u,
            errorMessage: "ADDRESS_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      province: {
        name: "province",
        label: "PROVINCE",
        type: "asyncAutocomplete",
        defaultValue: "",
        getOptions: getProvinces,
        sanitize: [
          {
            mode: SanitizeMode.ON_SUBMIT,
            sanitizeFn: (value) => value?.split(" - ")[1],
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
      },
      postalCode: {
        name: "postalCode",
        label: "POSTAL_CODE",
        type: "text",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: { value: 5, errorMessage: "POSTAL_CODE_SIZE" },
          minLength: { value: 5, errorMessage: "POSTAL_CODE_SIZE" },
          customValidation: {
            function: validatePostalCode,
            errorMessage: "POSTAL_CODE_SIZE",
          },
        },
      },
    },
  },
  footerLinks: ["PRIVACY", "COOKIE_POLICY", "RULES_OF_CONDUCT", "COPYRIGHT"],
  footerLogos: [DsaLogo],
  deliveryPriceKeys: ["retail", "deliveryTotal", "tax", "totalAmountToPay"],
  showHmpBenefits: true,
  showHmpPriceContent: true,
  showLinkToOrderConfirmation: true,
  showPdfContainer: true,
  supportedCountryCodes: ["th"],
  defaultCountryCode: "+66|Thailand",
  missingGsgStep: true,
};
